import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import type { ChartMarker } from './types';

// SAFETY: react-icons renders an SVG and accepts the standard SVG prop contract.
const SearchIcon = FiSearch as React.ComponentType<React.SVGProps<SVGSVGElement>>;

const ROW_HEIGHT = 48;
const VISIBLE_ROWS = 4;
const OVERSCAN = 3;

type Props = {
  fills: ChartMarker[];
  formatTime: (value: string | number) => string;
  formatNumber: (value: number | undefined, kind: 'quantity' | 'price') => string | null;
  onSelect?: (fill: ChartMarker) => void;
};

export default function ChartFillDetails({ fills, formatTime, formatNumber, onSelect }: Props) {
  const [query, setQuery] = useState('');
  const [jump, setJump] = useState('');
  const [start, setStart] = useState(0);
  const [selected, setSelected] = useState<ChartMarker | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const [visibleRows, setVisibleRows] = useState(VISIBLE_ROWS);
  useLayoutEffect(() => {
    const element = list.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      setVisibleRows(
        Math.min(VISIBLE_ROWS, Math.max(1, Math.floor(entry.contentRect.height / ROW_HEIGHT))),
      );
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const matches = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    if (!term) return fills;
    return fills.filter((fill) =>
      [
        fill.details?.identifier || fill.id,
        fill.details?.symbol,
        fill.action || fill.side,
        fill.details?.quantity,
        fill.details?.price ?? fill.value,
        formatTime(fill.details?.filledAt ?? fill.x),
      ]
        .join(' ')
        .toLocaleLowerCase()
        .includes(term),
    );
  }, [fills, query, formatTime]);
  const maximumStart = Math.max(0, matches.length - visibleRows);
  const first = Math.max(0, Math.min(start, maximumStart) - OVERSCAN);
  const last = Math.min(matches.length, first + visibleRows + OVERSCAN * 2);
  const scrollTo = (index: number) => {
    const next = Math.max(0, Math.min(index, maximumStart));
    if (list.current) list.current.scrollTop = next * ROW_HEIGHT;
    setStart(next);
  };
  const { quantity, priceRange } = useMemo(() => {
    let total = 0;
    let quantityAvailable = true;
    let pricesAvailable = fills.length > 0;
    let minimum = Infinity;
    let maximum = -Infinity;
    for (const fill of fills) {
      const amount = fill.details?.quantity;
      if (amount === undefined || !Number.isFinite(amount)) quantityAvailable = false;
      else total += amount;
      const price = fill.details?.price ?? fill.value;
      if (price === undefined || !Number.isFinite(price)) pricesAvailable = false;
      else {
        minimum = Math.min(minimum, price);
        maximum = Math.max(maximum, price);
      }
    }
    return {
      quantity: quantityAvailable ? total : undefined,
      priceRange: pricesAvailable ? { minimum, maximum } : null,
    };
  }, [fills]);
  return (
    <section className="chart-fill-details" aria-label="Fills at this chart marker">
      <div className="chart-fill-details__summary">
        <span>
          <small>Total quantity</small>
          <strong>{formatNumber(quantity, 'quantity') ?? 'Unavailable'}</strong>
        </span>
        <span>
          <small>
            Fill price{priceRange && priceRange.minimum !== priceRange.maximum ? ' range' : ''}
          </small>
          <strong>
            {priceRange
              ? `${formatNumber(priceRange.minimum, 'price')}${priceRange.minimum !== priceRange.maximum ? ` – ${formatNumber(priceRange.maximum, 'price')}` : ''}`
              : 'Unavailable'}
          </strong>
        </span>
      </div>
      <label className="chart-fill-details__search">
        <SearchIcon aria-hidden="true" />
        <input
          type="search"
          aria-label="Search fills in this marker"
          placeholder="Find a fill by time, quantity or ID"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            scrollTo(0);
          }}
        />
      </label>
      <div className="chart-fill-details__columns" aria-hidden="true">
        <span>Time</span>
        <span>Quantity</span>
        <span>Price</span>
      </div>
      <div
        ref={list}
        className="chart-fill-details__scroll"
        style={{ height: Math.min(Math.max(1, matches.length), VISIBLE_ROWS) * ROW_HEIGHT }}
        tabIndex={0}
        role="list"
        aria-label="Fills in this chart marker"
        onScroll={(event) => setStart(Math.floor(event.currentTarget.scrollTop / ROW_HEIGHT))}
        onKeyDown={(event) => {
          if (event.key === 'End' || event.key === 'Home') {
            event.preventDefault();
            scrollTo(event.key === 'End' ? maximumStart : 0);
          }
        }}
      >
        {matches.length === 0 ? (
          <p className="chart-fill-details__empty">
            No fills match. Try another time, quantity or ID.
          </p>
        ) : (
          <>
            <div aria-hidden="true" style={{ height: first * ROW_HEIGHT }} />
            {matches.slice(first, last).map((fill) => {
              const identifier = fill.details?.identifier || fill.id;
              return (
                <div role="listitem" key={fill.id}>
                  <button
                    type="button"
                    className="chart-fill-details__row"
                    data-fill-id={identifier}
                    aria-label={`Inspect fill ${identifier}`}
                    aria-pressed={selected?.id === fill.id}
                    onClick={() => {
                      setSelected(fill);
                      onSelect?.(fill);
                    }}
                  >
                    <span>{formatTime(fill.details?.filledAt ?? fill.x)}</span>
                    <span>{formatNumber(fill.details?.quantity, 'quantity') ?? '—'}</span>
                    <span>{formatNumber(fill.details?.price ?? fill.value, 'price') ?? '—'}</span>
                  </button>
                </div>
              );
            })}
            <div aria-hidden="true" style={{ height: (matches.length - last) * ROW_HEIGHT }} />
          </>
        )}
      </div>
      <div className="chart-fill-details__footer">
        <span role="status">
          {matches.length
            ? `${Math.min(start + 1, matches.length)}–${Math.min(start + visibleRows, matches.length)} of ${matches.length.toLocaleString()}`
            : '0 fills'}
          {query ? ' matching' : ''}
        </span>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            scrollTo(Number(jump) - 1);
          }}
        >
          <label>
            Jump to fill
            <input
              type="number"
              aria-label="Jump to fill"
              min={1}
              max={matches.length}
              value={jump}
              onChange={(event) => setJump(event.target.value)}
            />
          </label>
          <button
            type="submit"
            aria-label="Go to fill"
            disabled={!jump || Number(jump) < 1 || Number(jump) > matches.length}
          >
            Go
          </button>
        </form>
      </div>
      {selected && (
        <details className="chart-fill-details__selected" open>
          <summary>Selected fill</summary>
          <dl>
            <div>
              <dt>Fill ID</dt>
              <dd>{selected.details?.identifier || selected.id}</dd>
            </div>
            <div>
              <dt>Filled at</dt>
              <dd>{formatTime(selected.details?.filledAt ?? selected.x)}</dd>
            </div>
            <div>
              <dt>Quantity</dt>
              <dd>{formatNumber(selected.details?.quantity, 'quantity') ?? 'Unavailable'}</dd>
            </div>
            <div>
              <dt>Fill price</dt>
              <dd>
                {formatNumber(selected.details?.price ?? selected.value, 'price') ?? 'Unavailable'}
              </dd>
            </div>
            {selected.details?.reason && (
              <div>
                <dt>Reason</dt>
                <dd>{selected.details.reason}</dd>
              </div>
            )}
          </dl>
        </details>
      )}
    </section>
  );
}
